import { CHART_COLORS } from 'features/timeSeries/config/constants'
import { getSensorColor } from 'features/timeSeries/config/timeSeriesColors'
import React, { Dispatch, SetStateAction } from 'react'
import styled from 'styled-components'
import { EnhancedSensorEntry, TimeSeriesDerivedValueEntryJSON } from 'types'

import { legendLabel } from './chartLegendLabel'

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.75rem;
  padding: 0.375rem 0.75rem;
  background: ${CHART_COLORS.BACKGROUND};
  min-height: 1.5rem;
  align-items: center;
`

const LegendItem = styled.div<{ $dimmed: boolean; $highlighted: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  cursor: pointer;
  user-select: none;
  font-size: 0.7rem;
  line-height: 1;
  color: ${CHART_COLORS.TEXT};
  opacity: ${({ $dimmed }) => ($dimmed ? 0.4 : 1)};
  font-weight: ${({ $highlighted }) => ($highlighted ? 600 : 400)};
  white-space: nowrap;

  &:hover {
    opacity: ${({ $dimmed }) => ($dimmed ? 0.6 : 1)};
  }
`

const ColorDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`

interface ChartLegendProps {
  allSensorsToRender: Array<{ id: string; name: string }>
  enhancedSensorEntries: EnhancedSensorEntry[]
  derivedValues: TimeSeriesDerivedValueEntryJSON[]
  sensorRanges: Record<string, { min?: number; max?: number; unit: string }>
  sensorVisibilityConfig: Record<string, boolean>
  setSensorVisibilityConfig: Dispatch<SetStateAction<Record<string, boolean>>>
  customSensorColors: Record<string, string>
  highlightedSensorId: string | null
  setHighlightedSensorId: Dispatch<SetStateAction<string | null>>
}

export const ChartLegend: React.FC<ChartLegendProps> = ({
  allSensorsToRender,
  enhancedSensorEntries,
  derivedValues,
  sensorRanges,
  sensorVisibilityConfig,
  setSensorVisibilityConfig,
  customSensorColors,
  highlightedSensorId,
  setHighlightedSensorId,
}) => {
  if (allSensorsToRender.length === 0) return null

  const toggleVisibility = (itemId: string) => {
    setSensorVisibilityConfig((prev) => ({
      ...prev,
      [itemId]: !(prev[itemId] ?? true),
    }))
  }

  const renderLegendItem = (
    { id, name }: { id: string; name: string },
    index: number
  ) => {
    const color = customSensorColors[id] || getSensorColor(index)
    const isHidden = sensorVisibilityConfig[id] === false
    const isHighlighted = highlightedSensorId === id

    const label = legendLabel(
      { id, name },
      { derivedValues, sensorRanges, enhancedSensorEntries }
    )

    return (
      <LegendItem
        key={id}
        $dimmed={isHidden}
        $highlighted={isHighlighted}
        onClick={() => toggleVisibility(id)}
        onMouseEnter={() => setHighlightedSensorId(id)}
        onMouseLeave={() => setHighlightedSensorId(null)}>
        <ColorDot $color={color} />
        {label}
      </LegendItem>
    )
  }

  return (
    <LegendContainer>
      {allSensorsToRender.map(renderLegendItem)}
    </LegendContainer>
  )
}
